import { useState } from 'react';
import * as yup from 'yup';
import * as chanceApi from '../../lib/api/chance';
import * as productApi from '../../lib/api/product';
import { useSnackbar } from 'notistack';
import { useFormik } from 'formik';
// @mui
import DialogContent from '@mui/material/DialogContent';
import Box from '@mui/material/Box';
import LinearProgress from '@mui/material/LinearProgress';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';

import { createDraggablePaperComponent } from '../../components/common/dialog/DraggablePaper';
import BootstrapDialog, {
  BootstrapDialogTitle,
} from '../../components/common/dialog/BootstrapDialog';
import { useBoolean } from '../../hooks/useBoolean';
import SalesCostUploadForm from '../../components/sales-cost-manage/SalesCostUploadForm';
import UploadResultReview, {
  summarizeUploadResult,
} from '../../components/sales-cost-manage/UploadResultReview';

const PaperComponent = createDraggablePaperComponent('draggable-upload-dialog');

//application/vnd.openxmlformats-officedocument.spreadsheetml.sheet:xlsx, application/vnd.ms-excel:xls
const SUPPORTED_FORMATS = [
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
];

const ChanceCostCalcUploadDialog = ({
  title,
  open,
  onClose,
  refreshOnClose,
}) => {
  const isLoading = useBoolean();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { enqueueSnackbar } = useSnackbar();
  // null = 파일 선택 단계, 배열 = 업로드 후 결과 미리보기 단계
  const [uploadResult, setUploadResult] = useState(null);

  const initialValues = {
    file: null,
  };
  const validationSchema = yup.object({
    file: yup
      .mixed()
      .required('파일을 선택하세요')
      .test('fileFormat', '지정된 XLSX파일만 가능합니다.', (value) => {
        //console.log(value);
        return value && SUPPORTED_FORMATS.includes(value.type);
      }),
  });

  const formik = useFormik({
    initialValues: initialValues,
    enableReinitialize: true,
    validationSchema: validationSchema,
    onSubmit: (values, { setSubmitting }) => {
      setSubmitting(true);
      onSave(values);
      formik.resetForm();
      setSubmitting(false);
    },
  });

  const onSave = async (values) => {
    isLoading.onTrue();
    let formData = new FormData();
    formData.append('file', values.file);
    //console.log(formData, 'formData');

    try {
      const response = await chanceApi.uploadExl(formData);
      if (response.data.result === 'success') {
        // 바로 목록에 반영하지 않고 먼저 행별 결과(오류 포함)를 보여준다 -
        // "확인" 단계에서 오류 행을 제외하고 추가한다.
        setUploadResult(response.data.list || []);
      }
    } catch (err) {
      let message =
        typeof err.response !== 'undefined'
          ? err.response.data.message
          : err.message;
      enqueueSnackbar(message, {
        variant: 'error',
      });
    } finally {
      isLoading.onFalse();
    }
  };

  const resetDialog = () => {
    formik.resetForm();
    setUploadResult(null);
  };

  const handleClose = () => {
    resetDialog();
    onClose();
  };

  const isReviewStep = uploadResult !== null;

  /**
   * 신규 제품(matchStatus=NEW, 또는 SUGGEST에서 "신규로 등록"을 고른 행, __createProduct=true)은
   * 추가하기 전에 제품마스터에 먼저 등록해야 한다 - 제품정보 화면(ProductAddContainer)과 같은
   * 엔드포인트(POST /api/v1/master/product/info)를 그대로 쓴다. KMES 운영은 PRICE_TYP을
   * "매입 가격"/"소비자 가격" 고정값으로 쓰므로(ProductService#setProductMas) 매입단가/매출단가를
   * 그대로 price/priceConsume에 매핑한다 (2026-10-27 cost-excel-roundtrip, owner decision 3).
   *
   * 제품 등록은 제품정보 화면과 동일한 권한으로 막혀 있다 - 서버가 403을 돌려주면(현재는 제품
   * 등록 자체에 권한 가드가 없어 항상 통과하지만, 추후 가드가 생기면 그대로 적용된다) 그 행만
   * "제품 등록 권한 없음"으로 제외하고 나머지는 그대로 추가한다.
   *
   * [알려진 위험, Fable 사전검토 3-1] 이 "추가" 클릭 시점에 제품을 바로 등록하고, 원가 라인은
   * 그 뒤 사용자가 화면의 "저장"을 눌러야 저장된다. 사용자가 "추가"만 하고 저장 없이 화면을
   * 나가면 제품마스터에 "고아 제품"(원가 라인과 연결되지 않은 제품)이 남는다. 정석 해법은 신규
   * 제품 등록을 원가 "저장" 트랜잭션 안으로 옮기는 것(미리보기 "추가"는 스테이징만, 실제 등록은
   * 서버가 원가 저장과 함께 한 트랜잭션으로)이지만, 그러려면 새 백엔드 엔드포인트와 프론트
   * 스테이징 상태 설계가 필요해 이번 범위에서는 보류했다. 대신 지금 할 수 있는 선에서:
   * 1) ProductService#setProductMas를 @Transactional로 묶어 제품+가격 2건이 한 트랜잭션으로
   *    처리되게 했고(부분 실패로 제품만 남는 것은 막음),
   * 2) 등록 직전 이름 정확 일치 재조회로 같은 제품이 중복 생성되는 것을 막았다(ProductService
   *    #findExactNameMatch) - 두 사용자가 동시에 같은 신규 제품을 올려도 하나만 생성된다.
   * "저장 안 하면 제품만 남는다"는 위험 자체는 남아 있다 - 운영에서 고아 제품이 쌓이면 이름
   * 중복 체크가 자동으로 재사용하므로 눈에 보이는 중복은 생기지 않지만, 쓰이지 않는 제품마스터
   * 행은 주기적으로 점검해 정리해야 한다.
   */
  const registerNewProduct = async (row) => {
    const dataToSubmit = {
      prdTyp1: '-',
      prdTyp2: '-',
      itemCd: '',
      productLine: '',
      productSpec: '',
      productNm: (row.productNm || '').trim(),
      modelNm: (row.productNm || '').trim(),
      partnerUuid: row.sellPartnerUuid,
      category: '',
      price: row.priceVal, // 매입단가 -> "매입 가격"
      priceConsume: row.unitVal, // 매출단가 -> "소비자 가격"
      productUnit: row.productUnit,
      productDesc: row.productDesc,
      useYn: 'Y',
    };

    const res = await productApi.addProduct(dataToSubmit);
    return { productUuid: res.data?.productUuid, itemCd: res.data?.itemCd };
  };

  const handleConfirmAdd = async (validRows) => {
    const { errorRows, message } = summarizeUploadResult(uploadResult);

    const resolvedRows = [];
    const failedRows = [];

    for (const row of validRows) {
      if (!row.__createProduct) {
        resolvedRows.push(row);
        continue;
      }
      try {
        const { productUuid, itemCd } = await registerNewProduct(row);
        // [버그수정, Fable 사전검토 3-3/5] 신규 등록한 제품도 제품마스터에 연결된 행이니
        // masterTyp은 'N'(일반 항목)이어야 한다 - 'E'(기타)로 두면 확정 표에 "기타" 칩이
        // 붙고 패널에서 품목명을 자유 입력으로 고칠 수 있어 N형과 다르게 동작했다.
        resolvedRows.push({ ...row, productUuid, masterTyp: 'N', itemCd });
      } catch (err) {
        const isForbidden = err?.response?.status === 403;
        failedRows.push({
          productNm: row.productNm,
          reason: isForbidden
            ? '제품 등록 권한 없음'
            : err?.response?.data?.message || err.message,
        });
      }
    }

    if (resolvedRows.length > 0) {
      refreshOnClose(resolvedRows);
    }

    const extraParts = [message].filter(Boolean);
    if (failedRows.length > 0) {
      extraParts.push(
        `제품 등록 실패 ${failedRows.length}건: ${failedRows
          .map((f) => `${f.productNm}(${f.reason})`)
          .join(', ')}`,
      );
    }
    const extra = extraParts.join(' ');

    enqueueSnackbar(
      extra
        ? `정상 ${resolvedRows.length}행을 추가했습니다. ${extra}`
        : `${resolvedRows.length}행을 추가했습니다.`,
      {
        variant:
          errorRows.length > 0 || failedRows.length > 0 ? 'warning' : 'success',
      },
    );
    resetDialog();
  };

  return (
    <BootstrapDialog
      open={open}
      onClose={handleClose}
      PaperComponent={isMobile ? undefined : PaperComponent}
      aria-labelledby="draggable-upload-dialog"
      fullWidth={true}
      maxWidth={isReviewStep ? 'lg' : 'sm'}
      fullScreen={isMobile}
    >
      <BootstrapDialogTitle
        style={{ cursor: isMobile ? 'default' : 'move' }}
        id="draggable-upload-dialog"
        onClose={handleClose}
      >
        {title}
      </BootstrapDialogTitle>
      <DialogContent
        dividers
        sx={{
          minHeight: 200,
          maxHeight: isReviewStep ? { xs: 'none', sm: 560 } : 480,
          overflowY: 'auto',
        }}
      >
        {isLoading.value ? (
          <Box sx={{ width: '100%' }}>
            <LinearProgress />
          </Box>
        ) : isReviewStep ? (
          <UploadResultReview
            rows={uploadResult}
            onConfirm={handleConfirmAdd}
            onCancel={handleClose}
          />
        ) : (
          <SalesCostUploadForm formik={formik} />
        )}
      </DialogContent>
    </BootstrapDialog>
  );
};

export default ChanceCostCalcUploadDialog;
